// EPISTEME — Visualizations (canvas + SVG, zero deps, high quality)
// Probability distribution, Monte Carlo histogram, Trust graph

import type { Audit, EvidenceItem, TrustGraph, CausalModel } from './types';
import { averageCredibility, runMonteCarlo, summarizeMonteCarlo } from './epistemic';

export function drawCredenceDistribution(
  canvas: HTMLCanvasElement,
  credence: number,
  evidence: EvidenceItem[]
) {
  const ctx = canvas.getContext('2d', { alpha: true })!;
  const w = canvas.width = canvas.clientWidth * (window.devicePixelRatio || 1);
  const h = canvas.height = 118 * (window.devicePixelRatio || 1);
  ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
  const cw = canvas.clientWidth;
  const ch = 118;

  ctx.clearRect(0, 0, w, h);

  // background grid
  ctx.strokeStyle = '#27272a';
  ctx.lineWidth = 1;
  for (let x = 0; x <= 10; x++) {
    const px = (x / 10) * cw;
    ctx.beginPath();
    ctx.moveTo(px, 12);
    ctx.lineTo(px, ch - 18);
    ctx.stroke();
  }

  // approximate density using simple mixture around current credence + evidence impacts
  const points: number[] = [];
  const n = 180;
  let mass = 0;
  for (let i = 0; i < n; i++) {
    const p = i / (n - 1);
    let density = Math.exp(-Math.pow((p * 100 - credence) / (18 + evidence.length * 1.5), 2) * 0.8);

    for (const e of evidence) {
      const impact = (e.support === 'pro' ? 1 : e.support === 'con' ? -1 : 0.4) * averageCredibility(e) * 1.6;
      density += Math.exp(-Math.pow((p * 100 - (credence + impact * 2.5)) / 22, 2)) * 0.6;
    }
    points.push(density);
    mass += density;
  }

  // draw filled density
  ctx.fillStyle = 'rgba(196, 181, 253, 0.65)';
  ctx.beginPath();
  ctx.moveTo(0, ch - 18);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * cw;
    const y = 12 + (1 - (points[i] / (mass / 22))) * (ch - 30);
    ctx.lineTo(x, Math.max(14, Math.min(ch - 20, y)));
  }
  ctx.lineTo(cw, ch - 18);
  ctx.closePath();
  ctx.fill();

  // mean line
  const meanX = (credence / 100) * cw;
  ctx.strokeStyle = '#c4b5fd';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(meanX, 10);
  ctx.lineTo(meanX, ch - 16);
  ctx.stroke();

  // labels
  ctx.fillStyle = '#a1a1aa';
  ctx.font = '11px Inter, system-ui, sans-serif';
  ctx.fillText('0%', 4, ch - 4);
  ctx.fillText('100%', cw - 28, ch - 4);
  ctx.fillStyle = '#f5f5f5';
  ctx.fillText(`${credence}%`, meanX - 12, 9);
}

export function drawMonteCarlo(
  canvas: HTMLCanvasElement,
  audit: Audit
) {
  const results = runMonteCarlo(audit, 900);
  const summary = summarizeMonteCarlo(results);

  const ctx = canvas.getContext('2d', { alpha: true })!;
  const w = canvas.width = canvas.clientWidth * (window.devicePixelRatio || 1);
  const h = canvas.height = 132 * (window.devicePixelRatio || 1);
  ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
  const cw = canvas.clientWidth;
  const ch = 132;

  ctx.clearRect(0, 0, w, h);

  // axes
  ctx.strokeStyle = '#27272a';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(20, 18);
  ctx.lineTo(20, ch - 24);
  ctx.lineTo(cw - 8, ch - 24);
  ctx.stroke();

  // histogram buckets: -1 (bad) to +1 (good)
  const buckets = new Array(18).fill(0);
  for (const r of results) {
    const idx = Math.max(0, Math.min(17, Math.floor(((r + 1) / 2) * 17)));
    buckets[idx]++;
  }
  const maxB = Math.max(...buckets);

  const barW = (cw - 36) / 18;
  for (let i = 0; i < 18; i++) {
    const x = 22 + i * barW;
    const ht = (buckets[i] / maxB) * (ch - 48);
    ctx.fillStyle = i < 9 ? '#f87171' : '#4ade80';
    ctx.fillRect(x, ch - 26 - ht, Math.max(2, barW - 2), ht);
  }

  // summary text
  ctx.fillStyle = '#a1a1aa';
  ctx.font = '12px JetBrains Mono, monospace';
  ctx.fillText(`P(success if act) ≈ ${(summary.pSuccess * 100).toFixed(0)}%`, 24, 14);
  ctx.fillText(`EV: ${summary.expectedValue.toFixed(2)}`, cw - 92, 14);
}

// Draggable-ish trust graph (SVG)
export function renderTrustGraph(
  container: HTMLElement,
  graph: TrustGraph,
  onChange: (g: TrustGraph) => void
) {
  container.innerHTML = '';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '260');
  svg.style.display = 'block';
  svg.style.background = '#0a0a0a';
  svg.style.borderRadius = '10px';

  const NS = 'http://www.w3.org/2000/svg';
  const width = 520;
  const height = 260;
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

  // edges first
  for (const edge of graph.edges) {
    const a = graph.nodes.find(n => n.id === edge.from);
    const b = graph.nodes.find(n => n.id === edge.to);
    if (!a || !b) continue;

    const line = document.createElementNS(NS, 'line');
    line.setAttribute('x1', String(a.x ?? 80 + (graph.nodes.indexOf(a) % 4) * 110));
    line.setAttribute('y1', String(a.y ?? 70 + Math.floor(graph.nodes.indexOf(a) / 4) * 90));
    line.setAttribute('x2', String(b.x ?? 80 + (graph.nodes.indexOf(b) % 4) * 110));
    line.setAttribute('y2', String(b.y ?? 70 + Math.floor(graph.nodes.indexOf(b) / 4) * 90));
    line.setAttribute('stroke', '#3f3f46');
    line.setAttribute('stroke-width', '1.5');
    svg.appendChild(line);

    const midx = (parseFloat(line.getAttribute('x1')!) + parseFloat(line.getAttribute('x2')!)) / 2;
    const midy = (parseFloat(line.getAttribute('y1')!) + parseFloat(line.getAttribute('y2')!)) / 2 - 6;
    const txt = document.createElementNS(NS, 'text');
    txt.setAttribute('x', String(midx));
    txt.setAttribute('y', String(midy));
    txt.setAttribute('fill', '#71717a');
    txt.setAttribute('font-size', '9');
    txt.textContent = edge.relation;
    svg.appendChild(txt);
  }

  // nodes
  graph.nodes.forEach((node, idx) => {
    const g = document.createElementNS(NS, 'g');
    const x = node.x ?? (70 + (idx % 4) * 115);
    const y = node.y ?? (55 + Math.floor(idx / 4) * 85);

    const circle = document.createElementNS(NS, 'circle');
    circle.setAttribute('cx', String(x));
    circle.setAttribute('cy', String(y));
    circle.setAttribute('r', '18');
    circle.setAttribute('fill', node.incentive >= 1 ? '#052e16' : node.incentive <= -1 ? '#3f1f1f' : '#1f2937');
    circle.setAttribute('stroke', '#52525b');
    circle.setAttribute('stroke-width', '1.5');
    circle.classList.add('trust-node');

    const label = document.createElementNS(NS, 'text');
    label.setAttribute('x', String(x));
    label.setAttribute('y', String(y + 4));
    label.setAttribute('fill', '#e4e4e7');
    label.setAttribute('font-size', '9');
    label.setAttribute('text-anchor', 'middle');
    label.textContent = node.label.slice(0, 11);

    g.appendChild(circle);
    g.appendChild(label);

    // simple drag
    let dragging = false;
    circle.addEventListener('pointerdown', (e) => {
      dragging = true;
      (circle as any).setPointerCapture(e.pointerId);
    });
    window.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const rect = svg.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * width;
      const ny = ((e.clientY - rect.top) / rect.height) * height;
      node.x = Math.max(22, Math.min(width - 22, nx));
      node.y = Math.max(22, Math.min(height - 22, ny));
      onChange({ ...graph }); // trigger re-render
    });
    window.addEventListener('pointerup', () => { dragging = false; });

    // quick edit on double click
    g.addEventListener('dblclick', () => {
      const newLabel = prompt('Node label', node.label);
      if (newLabel) {
        node.label = newLabel;
        onChange({ ...graph });
      }
    });

    svg.appendChild(g);
  });

  container.appendChild(svg);
}

// Simple causal / influence diagram (similar interaction to trust graph)
export function renderCausalModel(
  container: HTMLElement,
  model: CausalModel,
  onChange: (m: CausalModel) => void
) {
  container.innerHTML = '';
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '240');
  svg.style.display = 'block';
  svg.style.background = '#0a0a0a';
  svg.style.borderRadius = '10px';

  const NS = 'http://www.w3.org/2000/svg';
  const width = 520;
  const height = 240;
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

  // edges
  model.edges.forEach(edge => {
    const a = model.nodes.find(n => n.id === edge.from);
    const b = model.nodes.find(n => n.id === edge.to);
    if (!a || !b) return;
    const x1 = a.x ?? 80 + (model.nodes.indexOf(a) % 4) * 110;
    const y1 = a.y ?? 60 + Math.floor(model.nodes.indexOf(a) / 4) * 80;
    const x2 = b.x ?? 80 + (model.nodes.indexOf(b) % 4) * 110;
    const y2 = b.y ?? 60 + Math.floor(model.nodes.indexOf(b) / 4) * 80;

    const line = document.createElementNS(NS, 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#4b5563');
    line.setAttribute('stroke-width', '1.5');
    svg.appendChild(line);

    if (edge.note) {
      const midx = (x1 + x2) / 2;
      const midy = (y1 + y2) / 2 - 8;
      const txt = document.createElementNS(NS, 'text');
      txt.setAttribute('x', String(midx));
      txt.setAttribute('y', String(midy));
      txt.setAttribute('fill', '#71717a');
      txt.setAttribute('font-size', '8');
      txt.textContent = edge.note.slice(0,18);
      svg.appendChild(txt);
    }
  });

  // nodes
  model.nodes.forEach((node, idx) => {
    const g = document.createElementNS(NS, 'g');
    const x = node.x ?? (70 + (idx % 4) * 115);
    const y = node.y ?? (50 + Math.floor(idx / 4) * 75);

    const rect = document.createElementNS(NS, 'rect');
    rect.setAttribute('x', String(x-28));
    rect.setAttribute('y', String(y-12));
    rect.setAttribute('width', '56');
    rect.setAttribute('height', '24');
    rect.setAttribute('rx', '6');
    rect.setAttribute('fill', '#1f2937');
    rect.setAttribute('stroke', '#52525b');
    rect.setAttribute('stroke-width', '1');

    const label = document.createElementNS(NS, 'text');
    label.setAttribute('x', String(x));
    label.setAttribute('y', String(y + 4));
    label.setAttribute('fill', '#e4e4e7');
    label.setAttribute('font-size', '9');
    label.setAttribute('text-anchor', 'middle');
    label.textContent = node.label.slice(0,12);

    g.appendChild(rect);
    g.appendChild(label);

    // drag support
    let dragging = false;
    rect.addEventListener('pointerdown', (e) => { dragging = true; (rect as any).setPointerCapture(e.pointerId); });
    window.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const rectEl = svg.getBoundingClientRect();
      const nx = ((e.clientX - rectEl.left) / rectEl.width) * width;
      const ny = ((e.clientY - rectEl.top) / rectEl.height) * height;
      node.x = Math.max(30, Math.min(width-30, nx));
      node.y = Math.max(20, Math.min(height-20, ny));
      onChange({ ...model });
    });
    window.addEventListener('pointerup', () => dragging = false);

    g.addEventListener('dblclick', () => {
      const nl = prompt('Variable name', node.label);
      if (nl) { node.label = nl; onChange({...model}); }
    });

    svg.appendChild(g);
  });

  container.appendChild(svg);
}
